import type { LanguageModel } from 'ai';
import type { SharedV4ProviderMetadata } from '@ai-sdk/provider';

import { Injectable, inject } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';
import { TranslateService } from '@ngx-translate/core';
import { wrapLanguageModel } from 'ai';
import { createOpenAICompatible } from '@ai-sdk/openai-compatible';

import { sendpulseProxyErrorSchema, sendpulseProxyErrorToText } from './sendpulse-ai-error.schema';
import { textToolProtocolMiddleware } from './text-tool-protocol.middleware';

export const SENDPULSE_AI_PROXY_METADATA_KEY = 'sendpulseAiProxy';

export interface SendpulseAiCredits {
    charged: number;
    balance: number;
    warning: string | null;
}

export interface SendpulseAiProxyMetadata {
    credits?: SendpulseAiCredits;
    customKeyFailed?: boolean;
}

/**
 * Builds an `@ai-sdk/openai-compatible` LanguageModel pointed at the documented
 * `POST /v1/proxy/chat/completions` backend proxy (MA auth). The endpoint is OpenAI-chat-
 * completions-compatible in its response shape, but requires SendPulse-specific request
 * fields (`user_id`/`product`) and returns a SendPulse-specific `credits` block instead of
 * model-token `usage`.
 *
 * `@ai-sdk/openai-compatible@3.0.5`'s actual implementation only reads `transformRequestBody`/
 * `metadataExtractor` from the TOP-LEVEL `createOpenAICompatible()` options — despite what the
 * type declarations claim, `provider.languageModel(modelId, config)`'s second argument is
 * silently ignored (`createLanguageModel = (modelId) => createChatModel(modelId)`, no config
 * param at all). So a fresh provider must be built per call with `userId`/`product` baked
 * directly into the top-level config, rather than overridden per model. `errorStructure` isn't
 * exposed via the public API in this version at all — errors are handled by inspecting the raw
 * response in the custom `fetch` below instead.
 */
@Injectable({ providedIn: 'root' })
export class SendpulseAiProviderService {
    private readonly cookieService = inject(CookieService);
    private readonly translateService = inject(TranslateService);

    public languageModel(modelId: string, product: string): LanguageModel {
        const provider = createOpenAICompatible({
            // `@ai-sdk/openai-compatible` builds request URLs via `new URL(`${baseURL}${path}`)`
            // with no base argument, so `baseURL` must be absolute (this app is CSR-only, so
            // `window.location.origin` is always available). The chat-completions `path` is
            // hardcoded inside the SDK to `/chat/completions`, so `baseURL` must include
            // everything up through `/v1/proxy` to land on the documented
            // `POST /v1/proxy/chat/completions` endpoint.
            baseURL: `${window.location.origin}/api/ai-router-service/v1/proxy`,
            // baseURL: `${window.location.origin}/api/ai-router-service/v1/proxy`,
            name: 'sendpulse-ai-proxy',
            fetch: (url, init) => this.authenticatedFetch(url, init),
            transformRequestBody: (body) => ({ ...body, product }),
            metadataExtractor: {
                extractMetadata: async ({ parsedBody }) => {
                    const body = parsedBody as { credits?: SendpulseAiCredits; custom_key_failed?: boolean };
                    if (!body?.credits && !body?.custom_key_failed) {
                        return undefined;
                    }
                    const metadata: SendpulseAiProxyMetadata = {
                        credits: body.credits,
                        customKeyFailed: body.custom_key_failed ?? false,
                    };
                    return { [SENDPULSE_AI_PROXY_METADATA_KEY]: metadata } as unknown as SharedV4ProviderMetadata;
                },
                createStreamExtractor: () => ({
                    // Streaming responses never carry a `credits` block per the documented
                    // contract (billing only completes after the connection closes) — nothing
                    // to accumulate here.
                    processChunk: () => undefined,
                    buildMetadata: () => undefined,
                }),
            },
        });

        // Adapts between the SDK's native tool-calling protocol and the text-emulated one this
        // gateway actually speaks: inbound leaked pseudo-XML becomes real tool-call parts,
        // outbound tool-call/tool-result messages become XML text (the wire rejects
        // `role: "tool"` for Claude models) — see the middleware for the verified details.
        return wrapLanguageModel({
            model: provider.languageModel(modelId),
            middleware: textToolProtocolMiddleware,
        });
    }

    /**
     * A failed response now becomes a translated, user-facing message — for the failures worth
     * explaining. It used to throw only when the body matched the documented envelope, and a 405
     * from an environment with no route to the AI router therefore produced a chat that answered
     * nothing at all: unparseable body, no message, and no error branch in
     * `ai-chat-drawer.component.html` for even the SDK's own message to appear in.
     *
     * Retryable statuses are deliberately passed through rather than thrown on — see
     * `sendpulseProxyErrorToText`.
     *
     * Translated HERE rather than in the drawer because the message must cross
     * `SendpulseChatTransport`'s error boundary, which flattens the thrown error to
     * `{ type: 'error', errorText }` — a plain string. A key would arrive at the template unresolved.
     */
    private async authenticatedFetch(url: RequestInfo | URL, init?: RequestInit): Promise<Response> {
        const token = this.cookieService.get('oauth_access_token');
        const headers = new Headers(init?.headers);
        if (token) {
            headers.set('Authorization', `Bearer ${token}`);
        }

        const response = await fetch(url, { ...init, headers });

        if (!response.ok) {
            // `clone()` so the body stays readable — on statuses this doesn't recognize the response
            // is still returned to the SDK below.
            const rawBody = await response
                .clone()
                .json()
                .catch(() => null);
            const parsed = rawBody ? sendpulseProxyErrorSchema.safeParse(rawBody) : null;
            const text = sendpulseProxyErrorToText(response.status, parsed?.success ? parsed.data : null);

            if (text) {
                throw new Error(this.translateService.instant(text.key, text.params));
            }
        }

        return response;
    }
}
